import { useState, useEffect, useRef } from 'react';
import {
    faArrowUpRightFromSquare, faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { useAuthContext } from 'contexts/AuthContext';
import { NSButton, NSTable } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import classNames from 'classnames';
import { FEATURE_IDS } from 'services/constants';
import { URLS } from 'services/urls';
import { AccessLevels } from 'types/auth';
import { Link } from 'react-router-dom';
import { useGetTimelines } from '../hooks/useGetTimeline';
import './Timeline.scss';
import 'react-datepicker/dist/react-datepicker.css';
import { TEMP_TIMELINE_ID_PREFIX, TimelineStatus } from '../../constants';
import TimelineActivityRow from './components/TimelineActivityRow/TimelineActivityRow';

type TimelineItem = {
    id: string;
    dealId: number;
    name: string;
    target: string | null;
    completed: string | null;
    status: TimelineStatus;
};

const Timeline = ({ dealId }: { dealId: number }) => {
    const { selectedOrganizationId, userOrganizations, isFeatureAccessibleToUser } = useAuthContext();

    const listRef = useRef<HTMLDivElement>(null);
    const [timelineData, setTimelineData] = useState<TimelineItem[]>([]);
    const [selectedTimelineId, setSelectedTimelineId] = useState<string | number | null>(null);

    const { data: timelines, isLoading: isLoadingTimelines } = useGetTimelines({ orgId: selectedOrganizationId!, dealId });

    useEffect(() => {
        if (timelineData?.length === 0 && timelines) {
            setTimelineData(timelines);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timelines]);

    const handleAddNewRow = () => {
        const tempId = `${TEMP_TIMELINE_ID_PREFIX}${Date.now()}`;
        setTimelineData(prev => [
            {
                id: tempId,
                dealId,
                name: '',
                target: null,
                completed: null,
                status: TimelineStatus.OPEN,
            },
            ...prev,
        ]);
        setSelectedTimelineId(tempId);
    };

    const selectedUserOrganization = userOrganizations.find(org => org.organization.correlationId === selectedOrganizationId);

    const dueDiligenceTemplateUrl
        = `${URLS.PROJECTS_APP.ORGANIZATION_MANAGEMENT}/:${selectedUserOrganization?.organization.id}/products/timeline-activities`;

    const userCanUpdateTemplate = isFeatureAccessibleToUser([FEATURE_IDS.DEAL_ORGANIZATION_MANAGEMENT], AccessLevels.READ_WRITE_ACCESS);
    return (
        <div className="px-2 pb-2">
            <div className="d-flex justify-content-between align-items-center">
                <h5 className="text-white font-16 font-weight-semibold">Due diligence timeline</h5>
                <div>
                    {userCanUpdateTemplate ? (
                        <Link
                            to={dueDiligenceTemplateUrl}
                        >
                            <NSButton
                                icon={faArrowUpRightFromSquare}
                                color="link"
                                className="text-primary px-0 heap-deal-dashboard-overview-manage-template"
                                featureId={FEATURE_IDS.DEAL_ORGANIZATION_MANAGEMENT}
                            >
                                <span className="ml-1">Manage template</span>
                            </NSButton>
                        </Link>
                    ) : (
                        <NSButton
                            id="manage-template-link"
                            icon={faArrowUpRightFromSquare}
                            color="link"
                            className="text-primary px-0 heap-deal-dashboard-overview-manage-template"
                            featureId={FEATURE_IDS.DEAL_ORGANIZATION_MANAGEMENT}
                            disabled
                        >
                            <span className="ml-1">Manage template</span>
                        </NSButton>
                    )}
                </div>
            </div>
            <div ref={listRef} className="TimelineTable__container">
                <NSTable className="TimelineTable" hover>
                    <thead>
                        <tr>
                            <th className="d-flex justify-content-between align-items-center">
                                <span>Activity</span>
                                <NSButton
                                    className={classNames(
                                        'TimelineTable__add-btn font-weight-bold cursor--pointer heap-deal-dashboard-overview-add-activity',
                                        {
                                            'TimelineTable__add-btn--disabled': !!selectedTimelineId,
                                        },
                                    )}
                                    callback={handleAddNewRow}
                                    ariaLabel="Add activity"
                                    iconOnly
                                    icon={faPlus}
                                    color="link"
                                    disabled={!!selectedTimelineId}
                                />
                            </th>
                            <th>Target</th>
                            <th>Date completed</th>
                            <th>Status</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <RenderIf isTrue={timelineData?.length > 0} isLoading={isLoadingTimelines}>
                            {timelineData?.map(item => (
                                <TimelineActivityRow
                                    key={item.id}
                                    item={item}
                                    dealId={dealId}
                                    selectedTimelineId={selectedTimelineId}
                                    setSelectedTimelineId={setSelectedTimelineId}
                                    onTimelineDataUpdate={setTimelineData}
                                />
                            ))}
                        </RenderIf>
                    </tbody>
                </NSTable>
            </div>
        </div>
    );
};

export default Timeline;
